/**
 * Liaison d'un compte Idenva depuis la PWA (docs/IDENVA-INTEGRATION.md §3.2) :
 * OIDC Authorization Code + PKCE, client public `klavox-web`, decouverte standard
 * (rien de propre a Hydra ni a Keycloak).
 *
 * Ce module va chercher un ID token chez l'emetteur et s'arrete la : c'est le
 * SERVEUR Klavox qui le verifie (`apps/server/src/oidc.ts`) et qui lie le `sub`
 * (`POST /v1/accounts/oidc/link`, avec la preuve du mot de passe maitre).
 *
 * Frontiere (§2) : rien du chemin crypto ne part vers l'emetteur -- ni en
 * parametre, ni dans `state`/`nonce`, qui sont des aleas sans lien avec le
 * coffre. Portee `openid` seule : Klavox n'a besoin que du `sub`, jamais de
 * l'e-mail (§3.4). L'access token rendu par l'emetteur est ignore.
 */

export const DEFAULT_IDENVA_CLIENT_ID = 'klavox-web';

/** Duree de vie d'une liaison commencee : au-dela, le retour de l'emetteur est refuse. */
export const IDENVA_LINK_TTL_MS = 10 * 60_000;

export type IdenvaLinkFailure =
  | 'config'
  | 'discovery'
  | 'refused'
  | 'redirect'
  | 'state'
  | 'issuer'
  | 'expired'
  | 'token'
  | 'network';

export class IdenvaLinkError extends Error {
  readonly reason: IdenvaLinkFailure;
  constructor(reason: IdenvaLinkFailure, detail: string) {
    // Le message ne recopie jamais un code, un verifier ni un jeton.
    super(`Liaison Idenva interrompue (${reason}) : ${detail}`);
    this.name = 'IdenvaLinkError';
    this.reason = reason;
  }
}

export interface IdenvaLinkConfig {
  /** Emetteur exact, tel que le serveur Klavox le verifie (`KLAVOX_OIDC_ISSUER`). */
  readonly issuer: string;
  readonly clientId?: string;
  /** URI de retour declaree chez Idenva pour ce client. */
  readonly redirectUri: string;
}

/**
 * Ce que la PWA garde entre le depart vers l'emetteur et son retour (stockage
 * de session de l'onglet). Le `codeVerifier` ne quitte l'appareil que vers le
 * point de jeton de l'emetteur.
 */
export interface PendingIdenvaLink {
  readonly issuer: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly tokenEndpoint: string;
  readonly state: string;
  readonly nonce: string;
  readonly codeVerifier: string;
  readonly expiresAt: number;
}

/** A transmettre tel quel a `linkOidcIdentity` (`sync-client.ts`). */
export interface IdenvaIdToken {
  readonly idToken: string;
  readonly nonce: string;
}

function b64u(octets: Uint8Array): string {
  let binaire = '';
  for (const octet of octets) binaire += String.fromCharCode(octet);
  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 octets du CSPRNG : 43 caracteres base64url (RFC 7636 §4.1, 43 a 128). */
function alea(): string {
  return b64u(crypto.getRandomValues(new Uint8Array(32)));
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

/** HTTPS obligatoire ; HTTP tolere sur la boucle locale seulement -- meme regle que le serveur. */
function secureUrl(raw: unknown): URL | undefined {
  if (typeof raw !== 'string') return undefined;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol === 'https:') return url;
  if (url.protocol === 'http:' && isLoopbackHost(url.hostname)) return url;
  return undefined;
}

async function postOrGet(url: string, init?: RequestInit): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(url, { ...init, redirect: 'error', cache: 'no-store' });
  } catch (cause) {
    throw new IdenvaLinkError('network', cause instanceof Error ? cause.message : 'requete reseau echouee');
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  if (!response.ok || typeof body !== 'object' || body === null || Array.isArray(body)) {
    throw new IdenvaLinkError(init === undefined ? 'discovery' : 'token', `reponse ${response.status} de l'emetteur`);
  }
  return body as Record<string, unknown>;
}

/**
 * Prepare le depart vers Idenva : decouverte, PKCE S256, `state` et `nonce`
 * frais. Rend l'URL ou envoyer l'utilisateur et ce qu'il faut garder pour son
 * retour.
 */
export async function beginIdenvaLink(
  config: IdenvaLinkConfig,
  now: number = Date.now(),
): Promise<{ readonly authorizationUrl: string; readonly pending: PendingIdenvaLink }> {
  const issuerUrl = secureUrl(config.issuer);
  if (issuerUrl === undefined) throw new IdenvaLinkError('config', "l'emetteur doit etre en https");
  if (secureUrl(config.redirectUri) === undefined) throw new IdenvaLinkError('config', "l'URI de retour doit etre en https");
  const clientId = config.clientId ?? DEFAULT_IDENVA_CLIENT_ID;

  const document = await postOrGet(config.issuer.replace(/\/+$/, '') + '/.well-known/openid-configuration');
  // Une decouverte qui annonce un autre emetteur est une confusion d'emetteurs (RFC 9207 §1).
  if (document.issuer !== config.issuer) {
    throw new IdenvaLinkError('discovery', 'la decouverte annonce un autre emetteur');
  }
  const authorizationEndpoint = secureUrl(document.authorization_endpoint);
  const tokenEndpoint = secureUrl(document.token_endpoint);
  // Le code et le verifier ne partent que chez l'emetteur, jamais chez un tiers qu'il designerait.
  if (
    authorizationEndpoint === undefined ||
    tokenEndpoint === undefined ||
    authorizationEndpoint.origin !== issuerUrl.origin ||
    tokenEndpoint.origin !== issuerUrl.origin
  ) {
    throw new IdenvaLinkError('discovery', "points d'autorisation ou de jeton hors de l'origine de l'emetteur");
  }
  const methods = document.code_challenge_methods_supported;
  if (Array.isArray(methods) && !methods.includes('S256')) {
    throw new IdenvaLinkError('discovery', "l'emetteur n'accepte pas PKCE S256");
  }

  const codeVerifier = alea();
  const challenge = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier)));
  const pending: PendingIdenvaLink = {
    issuer: config.issuer,
    clientId,
    redirectUri: config.redirectUri,
    tokenEndpoint: tokenEndpoint.href,
    state: alea(),
    nonce: alea(),
    codeVerifier,
    expiresAt: now + IDENVA_LINK_TTL_MS,
  };

  const url = new URL(authorizationEndpoint.href);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('scope', 'openid');
  url.searchParams.set('state', pending.state);
  url.searchParams.set('nonce', pending.nonce);
  url.searchParams.set('code_challenge', b64u(challenge));
  url.searchParams.set('code_challenge_method', 'S256');
  return { authorizationUrl: url.href, pending };
}

/**
 * Traite le retour de l'emetteur : verifie ou l'on revient, `state`, `iss`
 * (RFC 9207) et la fraicheur, puis echange le code contre l'ID token. Le
 * jeton n'est PAS verifie ici : c'est le serveur Klavox qui en a l'autorite.
 */
export async function finishIdenvaLink(
  callbackUrl: string,
  pending: PendingIdenvaLink,
  now: number = Date.now(),
): Promise<IdenvaIdToken> {
  const callback = new URL(callbackUrl);
  const expected = new URL(pending.redirectUri);
  if (callback.origin !== expected.origin || callback.pathname !== expected.pathname) {
    throw new IdenvaLinkError('redirect', "retour sur une autre adresse que l'URI declaree");
  }
  const params = callback.searchParams;
  // Sans `state` identique, ce retour n'est pas celui de cette liaison (CSRF de connexion).
  if (params.get('state') !== pending.state) throw new IdenvaLinkError('state', 'state inattendu');
  const iss = params.get('iss');
  if (iss !== null && iss !== pending.issuer) throw new IdenvaLinkError('issuer', 'retour signe par un autre emetteur');
  if (params.get('error') !== null) throw new IdenvaLinkError('refused', "l'emetteur a refuse la liaison");
  if (now > pending.expiresAt) throw new IdenvaLinkError('expired', 'liaison commencee il y a trop longtemps');
  const code = params.get('code');
  if (code === null || code.length === 0) throw new IdenvaLinkError('token', 'code absent du retour');

  const body = await postOrGet(pending.tokenEndpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: pending.redirectUri,
      client_id: pending.clientId,
      code_verifier: pending.codeVerifier,
    }).toString(),
  });
  if (typeof body.id_token !== 'string' || body.id_token.length === 0) {
    throw new IdenvaLinkError('token', "l'emetteur n'a pas rendu d'ID token");
  }
  return { idToken: body.id_token, nonce: pending.nonce };
}

/** Chemin de retour, sur l'origine de la PWA : l'URI declaree chez Idenva en est `<origine>/auth/callback`. */
export const IDENVA_CALLBACK_PATH = '/auth/callback';

/**
 * Canal entre la fenetre de liaison et l'onglet du coffre. `BroadcastChannel`
 * ne relie que des pages de la MEME origine, et ne depend pas de
 * `window.opener` -- qu'un emetteur avec `Cross-Origin-Opener-Policy` coupe.
 */
export const IDENVA_CALLBACK_CHANNEL = 'klavox-idenva';

const CALLBACK_MESSAGE = 'klavox.idenva.callback';

/** URI de retour de la PWA servie depuis `origin`. */
export function idenvaRedirectUri(origin: string): string {
  return new URL(IDENVA_CALLBACK_PATH, origin).href;
}

export function isIdenvaCallback(href: string): boolean {
  return new URL(href).pathname === IDENVA_CALLBACK_PATH;
}

/** Le strict necessaire de `BroadcastChannel`, pour pouvoir le remplacer en test. */
export interface IdenvaCallbackChannel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  close(): void;
}

/**
 * Page de retour, dans la fenetre de liaison : remet l'adresse a l'onglet du
 * coffre, et c'est tout. Elle n'echange rien et ne stocke rien -- le verifier
 * PKCE n'existe que dans la memoire de l'onglet qui a commence la liaison, seul
 * capable d'echanger le code.
 */
export function relayIdenvaCallback(href: string, channel: Pick<IdenvaCallbackChannel, 'postMessage' | 'close'>): void {
  channel.postMessage({ type: CALLBACK_MESSAGE, url: href });
  channel.close();
}

/** Aller-retour chez l'emetteur, sans rien lier : ce qu'il faut pour obtenir un ID token. */
export interface IdenvaSignInRun {
  readonly config: IdenvaLinkConfig;
  readonly channel: IdenvaCallbackChannel;
  /** Dirige vers l'emetteur la fenetre ouverte au clic (ouverte avant tout `await`, sinon le navigateur la bloque). */
  navigate(authorizationUrl: string): void;
  readonly signal?: AbortSignal;
  readonly now?: () => number;
}

export interface IdenvaLinkRun extends IdenvaSignInRun {
  /** Preuve du mot de passe maitre redemandee a l'utilisateur ; `null` = il renonce. */
  proveMasterPassword(): Promise<string | null>;
  /** `linkOidcIdentity` du serveur Klavox, session comprise. */
  link(token: IdenvaIdToken, masterPasswordHashBase64: string): Promise<void>;
}

/** Attend l'adresse de retour relayee par la fenetre de liaison, au plus jusqu'a l'expiration ; `null` si interrompu. */
function waitForCallback(
  channel: IdenvaCallbackChannel,
  signal: AbortSignal,
  pending: PendingIdenvaLink,
  now: number,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const finish = (): void => {
      clearTimeout(timer);
      channel.removeEventListener('message', onMessage);
      signal.removeEventListener('abort', onAbort);
    };
    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { type?: unknown; url?: unknown } | null;
      if (typeof data !== 'object' || data === null || data.type !== CALLBACK_MESSAGE || typeof data.url !== 'string') return;
      finish();
      resolve(data.url);
    };
    const onAbort = (): void => {
      finish();
      resolve(null);
    };
    const timer = setTimeout(() => {
      finish();
      reject(new IdenvaLinkError('expired', 'aucun retour de l emetteur dans le delai'));
    }, Math.max(0, pending.expiresAt - now));
    if (signal.aborted) {
      onAbort();
      return;
    }
    channel.addEventListener('message', onMessage);
    signal.addEventListener('abort', onAbort);
  });
}

/**
 * Connexion Idenva : depart vers l'emetteur, retour relaye par `/auth/callback`, verifications de
 * `finishIdenvaLink`. Rend l'ID token (que le SERVEUR Klavox verifiera), ou `null` si l'utilisateur renonce.
 * Sert a la liaison des Reglages comme a la creation / l'ouverture du coffre DEPUIS Idenva.
 */
export async function obtainIdenvaIdToken(run: IdenvaSignInRun): Promise<IdenvaIdToken | null> {
  const now = run.now ?? Date.now;
  const started = now();
  const { authorizationUrl, pending } = await beginIdenvaLink(run.config, started);
  // A l'ecoute AVANT de partir : un retour immediat (session Idenva deja ouverte) ne se perd pas.
  const stop = new AbortController();
  const forward = (): void => stop.abort();
  run.signal?.addEventListener('abort', forward);
  if (run.signal?.aborted === true) stop.abort();
  let callbackUrl: string | null;
  try {
    const waiting = waitForCallback(run.channel, stop.signal, pending, started);
    try {
      run.navigate(authorizationUrl);
    } catch (cause) {
      // Fenetre fermee ou bloquee : on cesse d'ecouter avant de remonter l'erreur.
      stop.abort();
      throw cause;
    }
    callbackUrl = await waiting;
  } finally {
    run.signal?.removeEventListener('abort', forward);
  }
  if (callbackUrl === null) return null;
  return finishIdenvaLink(callbackUrl, pending, now());
}

/**
 * Liaison complete, telle que la declenche le bouton « Lier mon compte
 * Idenva » : ID token (`obtainIdenvaIdToken`), puis preuve du mot de passe
 * maitre et liaison par le serveur. Rend `'cancelled'` si l'utilisateur
 * renonce ; toute autre issue que `'linked'` est une erreur levee.
 */
export async function runIdenvaLink(run: IdenvaLinkRun): Promise<'linked' | 'cancelled'> {
  const token = await obtainIdenvaIdToken(run);
  if (token === null) return 'cancelled';
  const proof = await run.proveMasterPassword();
  if (proof === null) return 'cancelled';
  await run.link(token, proof);
  return 'linked';
}
